import {
  TrashIcon,
  VideoCameraIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';

import CircleIcon from 'assets/CircleIcon';
import TextIcon from 'assets/TextIcon';
import CTAModal from 'components/CTAModal/CTAModal';
import Dropdown from 'components/Dropdown/Dropdown';
import Icon, { IconVariant } from 'components/Icon/Icon';
import useDeleteQuestions from 'hooks/useDeleteQuestions';
import { Question, QuestionType } from 'hooks/useGetCompetencyDetails';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';
import { secondsToTime } from 'utils/secondsToTime';

import CompetencyQuestionsForm from './CompetencyQuestionsForm';

interface QuestionCardProps {
  question: Question;
  showIconAnimation?: boolean;
  successfulUploadIds?: string[];
  onUpdateAndAddAnother: () => void;
}

const QuestionCard = ({
  question,
  showIconAnimation,
  successfulUploadIds = [],
  onUpdateAndAddAnother,
}: QuestionCardProps) => {
  const { interviewUid, competencyUid } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [animateIcon, setAnimateIcon] = useState(
    <CheckCircleIcon className="h-6 w-6 animate-pulse-size text-kira-green-800 transition ease-linear" />
  );
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { deleteQuestions, isLoading: isDeleteQuestionLoading } =
    useDeleteQuestions();
  const queryClient = useQueryClient();

  const isWrittenQuestion = question.question_type === QuestionType.WRITTEN;
  const isVideoQuestion = question.question_type === QuestionType.VIDEO;

  const icon: JSX.Element = useMemo(() => {
    if (isVideoQuestion) return <VideoCameraIcon />;
    return <TextIcon wrapperClassNames="h-4 w-4" />;
  }, [question.question_type]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!showIconAnimation) return () => {};

    const timeoutId = setTimeout(() => {
      setAnimateIcon(<Icon icon={icon} variant={IconVariant.MEDIUM} />);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [showIconAnimation]);

  const questionWrapperClassName = twMerge(
    'border',
    'border-kira-slate-200',
    'bg-kira-slate-50',
    'rounded-lg',
    'py-3',
    'px-4',
    'hover:border-kira-blue-800',
    'hover:bg-kira-blue-100',
    'transition',
    'group/question-card'
  );

  const buttonClassName = twMerge('flex', 'text-left', 'grow');

  const handleDeleteQuestions = async () => {
    if (!competencyUid) return;

    await deleteQuestions(
      {
        competencyUid,
        questionUid: question.uid,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Question deleted'),
            t('Question successfully deleted')
          );
          queryClient.invalidateQueries({
            queryKey: ['getCompetencyDetails', interviewUid, competencyUid],
          });
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while deleting the question'),
            error.message
          );
        },
      }
    );
    setIsModalOpen(false);
  };

  return isEditing ? (
    <CompetencyQuestionsForm
      initialValue={question}
      closeForm={() => {
        setIsEditing(false);
      }}
      showBanner={successfulUploadIds.includes(question.uid)}
      updateAndAddAnother={onUpdateAndAddAnother}
    />
  ) : (
    <div className={questionWrapperClassName} data-testid="questionCard">
      <div className="flex items-center justify-between">
        <button
          className={buttonClassName}
          type="button"
          onClick={() => setIsEditing(true)}
        >
          <div className="flex items-center">
            <div className="relative">
              {isVideoQuestion && question.video?.length === 0 && (
                <InformationCircleIcon className="absolute -right-0.5 -top-0.5 w-3 text-kira-yellow-900" />
              )}
              {showIconAnimation ? (
                animateIcon
              ) : (
                <Icon icon={icon} variant={IconVariant.MEDIUM} />
              )}
            </div>
            <div className="ml-2">
              <p
                className="prose-body-md-med text-kira-slate-900"
                data-testid="questionName"
              >
                {question.question_text}
              </p>
              <div className="prose-body-sm-reg flex items-center pt-1 text-kira-slate-700">
                {isVideoQuestion && !!question.preparation_time && (
                  <>
                    <p>
                      {secondsToTime(question.preparation_time)} {t('prep')}
                    </p>
                    <CircleIcon wrapperClassNames="inline-block mx-2" />
                  </>
                )}
                <p>
                  {question.response_time &&
                    secondsToTime(question.response_time)}
                  &nbsp;{t('response')}
                </p>
                {isWrittenQuestion && !!question.word_limit && (
                  <>
                    <CircleIcon wrapperClassNames="inline-block mx-2" />
                    <p data-testid="wordLimit">
                      {question.word_limit} {t('words')}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </button>
        <div className="ml-4 hidden items-center group-hover/question-card:flex">
          <Dropdown
            options={[
              {
                label: t('Delete'),
                icon: <TrashIcon className="h-3.5 w-3.5" />,
                onClick: () => {
                  setIsModalOpen(true);
                },
              },
            ]}
          />
        </div>
      </div>
      <CTAModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={t('Delete Question')}
        description={t('Deleting a question is permanent and can’t be undone.')}
        confirmation={t('Are you sure you want to delete it?')}
        action={handleDeleteQuestions}
        isLoading={isDeleteQuestionLoading}
        buttonDataTestId="modalDelete"
      />
    </div>
  );
};

export default QuestionCard;
